import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PromoteDialog } from '@/features/tracker/PromoteDialog'
import { useDemoteTicket, extractErrorMessage } from '@/queries/ticketMutations'
import { useIsAdmin } from '@/lib/auth'
import { trackerStatusLabels, trackerStatusVariants } from '@/lib/status'
import type { Ticket } from '@/types/ticket'

// Same layout convention as the Linear/AI sections in AdminControls (a
// `border-t pt-3` block containing a 68px-label grid row) — kept local here
// rather than importing AdminControls' private Field so this stays testable
// in isolation (see the isAdmin check below).
function Field({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[68px_1fr] items-center gap-2 text-xs">
      <span className="text-muted-foreground">Tracker</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

// Staff-only, like promote/demote on the server: checked here (not just by
// the caller's route guard) so "Track this issue" is verifiably absent for a
// non-admin regardless of where this ever ends up being rendered.
export function TrackerControls({ ticket }: { ticket: Ticket }) {
  const isAdmin = useIsAdmin()
  const demote = useDemoteTicket(ticket._id)

  if (!isAdmin) return null

  return (
    <div className="border-t pt-3">
      <Field>
        {ticket.tracker ? (
          <div className="flex items-center gap-2">
            <Badge variant={trackerStatusVariants[ticket.tracker.status]}>
              {trackerStatusLabels[ticket.tracker.status]}
            </Badge>
            <Button
              size="xs"
              variant="outline"
              disabled={demote.isPending}
              onClick={async () => {
                if (!confirm('Stop tracking this issue? It will no longer be visible to the client company.')) return
                try {
                  await demote.mutateAsync()
                  toast.success('Tracking stopped')
                } catch (err) {
                  toast.error(extractErrorMessage(err, 'Could not stop tracking'))
                }
              }}
            >
              Stop tracking
            </Button>
          </div>
        ) : (
          <PromoteDialog
            ticketId={ticket._id}
            companyId={ticket.companyId}
            trigger={
              <Button size="xs" variant="outline" className="w-full justify-start">
                Track this issue
              </Button>
            }
          />
        )}
      </Field>
    </div>
  )
}
